/**
 * Pricing page
 */

import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface PricingPlan {
  name: string;
  price: {monthly: number; annual: number};
  description: string;
  features: string[];
  cta: {label: string; href: string; variant?: 'primary' | 'secondary'};
  popular?: boolean;
  recommended?: boolean;
}

const individualPlans: PricingPlan[] = [
  {
    name: 'Free',
    price: {monthly: 0, annual: 0},
    description: 'Perfect for trying out VoxaPilot',
    features: [
      '10 hours of transcription/month',
      'Basic AI summaries',
      'Up to 3 team members',
      'Email support',
    ],
    cta: {label: 'Start Free', href: ROUTES.SIGN_IN},
  },
  {
    name: 'Pro',
    price: {monthly: 29, annual: 290},
    description: 'For individuals and small teams',
    features: [
      '100 hours of transcription/month',
      'Advanced AI summaries',
      'Unlimited team members',
      'Priority support',
      'Integrations',
      'Custom retention',
    ],
    cta: {label: 'Get Started', href: `${ROUTES.SIGN_IN}?plan=pro`},
  },
  {
    name: 'Pro+',
    price: {monthly: 59, annual: 590},
    description: 'For power users',
    features: [
      '300 hours of transcription/month',
      'Premium AI features',
      'Advanced analytics',
      'Priority support',
      'All integrations',
      'Extended retention',
    ],
    cta: {label: 'Get Pro+', href: `${ROUTES.SIGN_IN}?plan=pro-plus`},
    recommended: true,
  },
  {
    name: 'Ultra',
    price: {monthly: 199, annual: 1990},
    description: 'For professionals who need it all',
    features: [
      'Unlimited transcription',
      'All premium features',
      'Priority access to new features',
      'Dedicated support',
      'Custom integrations',
      'Unlimited retention',
    ],
    cta: {label: 'Get Ultra', href: `${ROUTES.SIGN_IN}?plan=ultra`},
  },
];

const businessPlans: PricingPlan[] = [
  {
    name: 'Teams',
    price: {monthly: 49, annual: 490},
    description: 'For growing teams',
    features: [
      'Everything in Pro+',
      'Centralized team billing',
      'Usage analytics and reporting',
      'Org-wide privacy controls',
      'Role-based access control',
      'SAML/OIDC SSO',
    ],
    cta: {label: 'Get Teams', href: `${ROUTES.SIGN_IN}?plan=teams`},
  },
  {
    name: 'Enterprise',
    price: {monthly: 0, annual: 0}, // Custom pricing
    description: 'For large organizations',
    features: [
      'Everything in Teams',
      'Pooled usage',
      'Invoice/PO billing',
      'SCIM seat management',
      'AI code tracking API',
      'Granular admin controls',
      'Priority support & account management',
    ],
    cta: {label: 'Contact Sales', href: ROUTES.ENTERPRISE},
  },
];

const companyLogos = [
  {name: 'TechCorp', logo: 'TC'},
  {name: 'StartupXYZ', logo: 'SX'},
  {name: 'BigCo', logo: 'BC'},
  {name: 'InnovateLabs', logo: 'IL'},
  {name: 'DataFlow', logo: 'DF'},
  {name: 'CloudSync', logo: 'CS'},
];

const pricingFAQ = [
  {
    question: 'What is the right plan for me?',
    answer: 'Start with our Free plan to try VoxaPilot. If you need more transcription hours and advanced features, Pro is perfect for individuals. For teams, choose Teams or Enterprise for collaboration features and admin controls.',
  },
  {
    question: 'What are my payment options?',
    answer: 'We accept all major credit cards, debit cards, and for Enterprise plans, we also accept ACH transfers and wire transfers. You can pay monthly or annually (save 20% with annual billing).',
  },
  {
    question: 'How does usage-based pricing work?',
    answer: 'Each plan includes a monthly allowance of transcription hours. If you exceed your limit, you can either upgrade your plan or purchase additional hours. We\'ll notify you before you reach your limit.',
  },
  {
    question: 'How can I see and manage VoxaPilot\'s usage in my organization?',
    answer: 'Business and Enterprise plans include usage analytics and reporting. Admins can view team usage, set limits, and manage billing from the admin dashboard.',
  },
  {
    question: 'How does VoxaPilot use my data?',
    answer: 'Your meeting data is encrypted in transit and at rest. We never share your data with third parties. For more details, see our Privacy Policy and Security page.',
  },
  {
    question: 'Where can I ask more questions?',
    answer: 'Contact our support team at support@voxapilot.com or visit our Help Center. Enterprise customers have access to dedicated account managers.',
  },
];

function PricingFAQItem({question, answer, index}: {question: string; answer: string; index: number}) {
  const [isOpen, setIsOpen] = useState(false);
  const {ref, isVisible} = useScrollAnimation();

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`border-t border-gray-200 dark:border-gray-700 first:border-t-0 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{transitionDelay: `${index * 0.1}s`}}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-[#252526] transition-colors"
      >
        <span className="text-base font-medium text-textPrimary dark:text-[#CCCCCC] pr-8">{question}</span>
        <svg
          className={`w-5 h-5 text-textSecondary dark:text-[#858585] flex-shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pb-6 pr-12">
          <p className="text-textSecondary dark:text-[#858585] leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
}

function PricingCard({plan, billingCycle, index, parentVisible}: {plan: PricingPlan; billingCycle: 'monthly' | 'annual'; index: number; parentVisible: boolean}) {
  const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.annual;
  const priceDisplay =
    plan.name === 'Free'
      ? 'Free'
      : price === 0
      ? 'Custom'
      : `$${price}${billingCycle === 'monthly' ? '/mo' : '/yr'}`;

  return (
    <div
      className={`transition-all duration-700 ${
        parentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
    >
    <Card
      hover={plan.recommended}
      className={`p-8 relative flex flex-col h-full ${plan.recommended ? 'border-2 border-gray-900 dark:border-gray-700' : ''}`}
    >
      {plan.recommended && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-1 rounded-full text-xs font-semibold">
            Recommended
          </span>
        </div>
      )}

      <div className="flex flex-col h-full space-y-6">
        {/* Header */}
        <div>
          <h3 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">{plan.name}</h3>
          <p className="text-textSecondary dark:text-[#858585] text-sm mb-4">{plan.description}</p>
          <div className="flex items-baseline">
            <span className="text-4xl font-bold text-textPrimary dark:text-[#CCCCCC]">{priceDisplay}</span>
            {price > 0 && billingCycle === 'annual' && (
              <span className="ml-2 text-sm text-textTertiary dark:text-[#6A6A6A] line-through">
                ${plan.price.monthly}/mo
              </span>
            )}
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-3 flex-1">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start text-sm text-textSecondary dark:text-[#858585]">
              <span className="mr-2 text-gray-900 dark:text-gray-400 mt-0.5">✓</span>
              {feature}
            </li>
          ))}
        </ul>

        {/* CTA - Always at bottom */}
        <div className="mt-auto pt-4">
          <Link to={plan.cta.href} className="block">
            <Button
              variant={plan.recommended ? 'primary' : 'outline'}
              size="lg"
              className={`w-full ${plan.recommended ? 'bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white' : ''}`}
            >
              {plan.cta.label}
            </Button>
          </Link>
        </div>
      </div>
    </Card>
    </div>
  );
}

export function PricingPage() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const {ref: headerRef, isVisible: headerVisible} = useScrollAnimation();
  const {ref: toggleRef, isVisible: toggleVisible} = useScrollAnimation();
  const {ref: individualRef, isVisible: individualVisible} = useScrollAnimation();
  const {ref: businessRef, isVisible: businessVisible} = useScrollAnimation();
  const {ref: trustedRef, isVisible: trustedVisible} = useScrollAnimation();
  const {ref: faqRef, isVisible: faqVisible} = useScrollAnimation();

  return (
    <LandingLayout>
      <Section padding="lg" background="white" className="pt-32 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={headerRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <SectionHeader
              title="Pricing"
              subtitle="Choose the plan that works for your team"
              centered
              size="lg"
            />
          </div>

          {/* Billing Toggle */}
          <div
            ref={toggleRef as React.RefObject<HTMLDivElement>}
            className={`flex items-center justify-center gap-4 mb-8 mt-2 transition-all duration-700 ${
              toggleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{transitionDelay: '200ms'}}
          >
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-gray-900 dark:bg-gray-700 text-white'
                  : 'text-textSecondary dark:text-[#858585] hover:text-textPrimary dark:hover:text-[#CCCCCC] bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700'
              }`}
            >
              Monthly
            </button>
            <div className="relative">
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center transition-colors"
              >
                <div
                  className={`w-5 h-5 bg-gray-900 dark:bg-gray-300 rounded-full transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-gray-900 dark:bg-gray-700 text-white'
                  : 'text-textSecondary dark:text-[#858585] hover:text-textPrimary dark:hover:text-[#CCCCCC] bg-white dark:bg-[#252526] border border-gray-200 dark:border-gray-700'
              }`}
            >
              Annual
              <span className="ml-2 text-xs text-green-600 dark:text-green-500 font-medium">(Save 20%)</span>
            </button>
          </div>

          {/* Individual Plans - Left Aligned */}
          <div ref={individualRef as React.RefObject<HTMLDivElement>} className="mb-20 mt-8">
            <h2
              className={`text-3xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4 transition-all duration-700 ${
                individualVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Individual Plans
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
              {individualPlans.map((plan, index) => (
                <PricingCard key={plan.name} plan={plan} billingCycle={billingCycle} index={index} parentVisible={individualVisible} />
              ))}
            </div>
          </div>

          {/* Business Plans - Left Aligned */}
          <div ref={businessRef as React.RefObject<HTMLDivElement>} className="mb-20">
            <h2
              className={`text-3xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4 transition-all duration-700 ${
                businessVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Business Plans
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
              {businessPlans.map((plan, index) => (
                <PricingCard key={plan.name} plan={plan} billingCycle={billingCycle} index={index} parentVisible={businessVisible} />
              ))}
            </div>
          </div>

          {/* Trusted By Section */}
          <div
            ref={trustedRef as React.RefObject<HTMLDivElement>}
            className={`text-center mb-20 transition-all duration-700 ${
              trustedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-lg text-textSecondary dark:text-[#858585] mb-8">
              Trusted every day by millions of professional developers.
            </p>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-6 max-w-4xl mx-auto">
              {companyLogos.map((company, index) => (
                <div
                  key={company.name}
                  className={`px-6 py-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#252526] text-textSecondary dark:text-[#CCCCCC] font-semibold text-sm hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300 flex items-center justify-center ${
                    trustedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${index * 0.05}s`}}
                >
                  {company.logo}
                </div>
              ))}
            </div>
          </div>

          {/* Pricing FAQ */}
          <div ref={faqRef as React.RefObject<HTMLDivElement>} className="max-w-6xl mt-20">
            <div className="grid md:grid-cols-3 gap-12">
              {/* Left Column - Title */}
              <div
                className={`md:col-span-1 transition-all duration-700 ${
                  faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC]">Questions & Answers</h2>
              </div>

              {/* Right Column - FAQ Items */}
              <div className="md:col-span-2 space-y-0">
                {pricingFAQ.map((item, index) => (
                  <PricingFAQItem key={index} question={item.question} answer={item.answer} index={index} />
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

